import {useEffect, useRef, useState} from 'react';
import {cdmMw} from '../api';
import {SymbolTypeaheadResponse, SymbolTypeaheadResult} from '../types/symbolTypeahead.types';

interface UseSymbolTypeaheadOptions {
    limit?: number;
    symbolType?: string;
    debounceMs?: number;
}

interface UseSymbolTypeaheadReturn {
    results: SymbolTypeaheadResult[];
    isLoading: boolean;
    error: string | null;
}

/**
 * Custom hook for symbol typeahead functionality
 *
 * Features:
 * - Debounced search (default 300ms)
 * - Loading state management
 * - Error handling
 * - Cleanup on unmount
 *
 * @param query - Search query string
 * @param options - Configuration options
 * @returns Search results, loading state, and error
 */
export const useSymbolTypeahead = (
    query: string,
    options: UseSymbolTypeaheadOptions = {}
): UseSymbolTypeaheadReturn => {
    const {
        limit = 20,
        symbolType,
        debounceMs = 300
    } = options;

    const [results, setResults] = useState<SymbolTypeaheadResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const debounceTimer = useRef<number | null>(null);
    const abortController = useRef<AbortController | null>(null);

    useEffect(() => {
        // Reset if query is too short
        if (!query || query.length < 1) {
            setResults([]);
            setIsLoading(false);
            setError(null);
            return;
        }

        // Clear previous timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Set loading state immediately for better UX
        setIsLoading(true);
        setError(null);

        // Debounce the API call
        debounceTimer.current = window.setTimeout(async () => {
            // Cancel previous request if still pending
            if (abortController.current) {
                abortController.current.abort();
            }

            abortController.current = new AbortController();

            try {
                const response = await cdmMw.get<SymbolTypeaheadResponse>(
                    '/api/symbol-typeahead',
                    {
                        params: {
                            query,
                            limit,
                            ...(symbolType && {symbolType})
                        }
                    }
                );

                setResults(response.data.data);
                setIsLoading(false);
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    setError(err.message || 'Failed to fetch symbols');
                    setResults([]);
                    setIsLoading(false);
                }
            }
        }, debounceMs);

        // Cleanup
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, [query, limit, symbolType, debounceMs]);

    return { results, isLoading, error };
};
